import {
  FormFieldBoolSelect,
  FormFieldDateRangeSelect,
  FormFieldDateSelect,
  FormFieldDateTimeSelect,
  FormFieldFileUpload,
  FormFieldFullSelect,
  FormFieldInfo,
  FormFieldMonthSelect,
  FormFieldMultiSelect,
  FormFieldNumberInput,
  FormFieldPickList,
  FormFieldRadioSelect,
  FormFieldRichTextEditor,
  FormFieldSelectDropdown,
  FormFieldTextAreaInput,
  FormFieldTimeSelect,
  FormFieldYearSelect,
  FormFieldTextInput
} from "#components";
import { InputType } from "~/scad-shared/types/generic";

export interface ComponentConfig {
  component: any;
  clientSideOnly?: boolean;
  configureBindings?: (bindings: Indexable, props: any) => void;
}

export type ComponentMapType = Partial<Record<InputType, ComponentConfig>>;

export const defaultComponentMap: ComponentMapType = {
  // Simple mappings (no special bindings)
  [InputType.BoolSelect]: { component: FormFieldBoolSelect },
  [InputType.TimeSelect]: { component: FormFieldTimeSelect },
  [InputType.TextAreaInput]: { component: FormFieldTextAreaInput },
  [InputType.InputText]: { component: FormFieldTextInput },
  [InputType.NumberInput]: { component: FormFieldNumberInput },
  [InputType.IntegerInput]: { component: FormFieldNumberInput },
  [InputType.RichText]: { component: FormFieldRichTextEditor },
  [InputType.DateSelect]: { component: FormFieldDateSelect },
  [InputType.DateRange]: { component: FormFieldDateRangeSelect },
  [InputType.DateTimeSelect]: { component: FormFieldDateTimeSelect },
  [InputType.YearSelect]: { component: FormFieldYearSelect },
  [InputType.MonthSelect]: { component: FormFieldMonthSelect },

  // MultiSelect
  [InputType.MultiSelect]: {
    component: FormFieldMultiSelect,
    configureBindings: (bindings, props) => {
      bindings.tokenData = props.misc?.tokenData ?? {};
      bindings.fetchOptions = props.misc?.fetchOptions;
      bindings.endpoint = `/scad${props.misc?.endpoint}`;
      bindings.filter = props.misc?.filter;
      bindings.displayKey = props.misc?.displayKey;
      bindings.valueKey = props.misc?.valueKey;
      bindings.responseKey = props.misc?.responseKey;
      bindings.limit = -1;
      bindings.filterEnabled = true;
      bindings.options = props.misc?.options;
      bindings.editable = props.misc?.editable;
    },
  },

  // PickList
  [InputType.PickList]: {
    component: FormFieldPickList,
    configureBindings: (bindings, props) => {
      bindings.tokenData = props.misc?.tokenData ?? {};
      bindings.fetchOptions = props.misc?.fetchOptions;
      bindings.endpoint = `/scad${props.misc?.endpoint}`;
      bindings.filter = props.misc?.filter;
      bindings.displayKey = props.misc?.displayKey;
      bindings.valueKey = props.misc?.valueKey;
      bindings.responseKey = props.misc?.responseKey;
      bindings.limit = props.misc?.limit;
      bindings.options = props.misc?.options;
      bindings.editable = props.misc?.editable;
    },
  },

  // SelectDropdown
  [InputType.SelectDropdown]: {
    component: FormFieldSelectDropdown,
    configureBindings: (bindings, props) => {
      bindings.tokenData = props.misc?.tokenData ?? {};
      bindings.fetchOptions = props.misc?.fetchOptions;
      bindings.endpoint = `/scad${props.misc?.endpoint}`;
      bindings.filter = props.misc?.filter;
      bindings.displayKey = props.misc?.displayKey;
      bindings.valueKey = props.misc?.valueKey;
      bindings.responseKey = props.misc?.responseKey;
      bindings.limit = props.misc?.limit;
      bindings.options = props.misc?.options;
      bindings.editable = props.misc?.editable;
      bindings.defaultExpression = props.defaultExpression;
    },
  },

  // SelectQuery
  [InputType.SelectQuery]: {
    component: FormFieldSelectDropdown,
    configureBindings: (bindings, props) => {
      bindings.tokenData = props.misc?.tokenData ?? {};
      bindings.fetchOptions = props.misc?.fetchOptions;
      bindings.endpoint = `/scad${props.misc?.endpoint}`;
      bindings.filter = props.misc?.filter;
      bindings.displayKey = props.misc?.displayKey;
      bindings.valueKey = props.misc?.valueKey;
      bindings.responseKey = props.misc?.responseKey;
      bindings.limit = props.misc?.limit;
      bindings.options = props.misc?.options;
      bindings.editable = props.misc?.editable;
      bindings.defaultExpression = props.defaultExpression;
      bindings.filterQueryType = "serialized";
    },
  },

  // SelectTable
  [InputType.SelectTable]: {
    component: FormFieldFullSelect,
    configureBindings: (bindings, props) => {
      bindings.tokenData = props.misc?.tokenData ?? {};
      bindings.fetchOptions = props.misc?.fetchOptions;
      bindings.endpoint = `/scad${props.misc?.endpoint}`;
      bindings.filter = props.misc?.filter;
      bindings.displayKey = props.misc?.displayKey;
      bindings.valueKey = props.misc?.valueKey;
      bindings.responseKey = props.misc?.responseKey;
      bindings.limit = props.misc?.limit;
      bindings.options = props.misc?.options;
      bindings.editable = props.misc?.editable;
      bindings.defaultExpression = props.defaultExpression;
      bindings.fkTableName = props.misc?.fkTableName;
      bindings.fkSchemaName = props.misc?.fkSchemaName;
    },
  },

  // RadioSelect
  [InputType.RadioSelect]: {
    component: FormFieldRadioSelect,
    configureBindings: (bindings, props) => {
      bindings.tokenData = props.misc?.tokenData ?? {};
      bindings.fetchOptions = props.misc?.fetchOptions;
      bindings.endpoint = `/scad${props.misc?.endpoint}`;
      bindings.filter = props.misc?.filter;
      bindings.displayKey = props.misc?.displayKey;
      bindings.valueKey = props.misc?.valueKey;
      bindings.responseKey = props.misc?.responseKey;
      bindings.limit = props.misc?.limit;
      bindings.options = props.misc?.options;
    },
  },

  // EnumSelect
  [InputType.EnumSelect]: {
    component: FormFieldSelectDropdown,
    configureBindings: (bindings, props) => {
      bindings.fetchOptions = props.misc?.fetchOptions;
      bindings.endpoint = `/scad/meta/enums/${props.misc?.endpoint}`;
      bindings.displayKey = "Description";
      bindings.valueKey = "Value";
      bindings.responseKey = "EnumEntries";
      bindings.limit = props.misc?.limit;
    },
  },

  // DecimalInput
  [InputType.DecimalInput]: {
    component: FormFieldNumberInput,
    configureBindings: (bindings) => {
      bindings.minFractionDigits = 1;
      bindings.maxFractionDigits = 20;
    },
  },

  // PercentageInput
  [InputType.PercentageInput]: {
    component: FormFieldNumberInput,
    configureBindings: (bindings) => {
      bindings.minFractionDigits = 1;
      bindings.maxFractionDigits = 3;
      bindings.step = 0.01;
      bindings.suffix = "%";
    },
  },

  // CurrencyInput
  [InputType.CurrencyInput]: {
    component: FormFieldNumberInput,
    configureBindings: (bindings) => {
      bindings.minFractionDigits = 2;
      bindings.maxFractionDigits = 2;
      bindings.step = 0.01;
    },
  },

  // FileUpload
  [InputType.FileUpload]: {
    component: FormFieldFileUpload,
    clientSideOnly: false,
    configureBindings: (bindings, props) => {
      bindings.url = props.misc?.url;
      bindings.lookupUrl = props.misc?.lookupUrl;
    },
  },

  // Info
  [InputType.Info]: {
    component: FormFieldInfo,
    configureBindings: (bindings, props) => {
      bindings.groupId = props.misc?.endpointId;
    },
  },
};

export const defaultFallbackComponent: ComponentConfig = {
  component: FormFieldTextInput,
};
